import type { HomeResponse } from '@/client';
import {
    illustrationSlugForSeed,
    illustrationUrl,
    type IllustrationSlug,
} from '@/lib/illustrations';

/**
 * Which picture stands in for a home.
 *
 * Order of preference:
 *   1. the members' own cover photo — always beats a stock drawing
 *   2. an illustration matched to the home's type
 *   3. a deterministic pick from the remaining illustrations
 *
 * The artwork, the build-time glob and the stable-pick hash live in
 * `lib/illustrations.ts` — the Group Hub draws from the same set with only a
 * seed to go on. What is home knowledge, and stays here, is step 2.
 */

export { ILLUSTRATION_SLUGS, type IllustrationSlug } from '@/lib/illustrations';

/**
 * Backend `home_type.code` → slug. The codes are the seeded seven
 * (app/seeding/reference_data.py); the assets are ten, so three are
 * fallback-only.
 */
const SLUG_BY_HOME_TYPE: Record<string, IllustrationSlug> = {
    single_family: 'suburban-family-home',
    townhouse: 'city-townhouse',
    condo: 'modern-condo',
    apartment: 'urban-apartment',
    villa: 'coastal-cottage',
    cabin: 'mountain-cabin',
    loft: 'garden-duplex',
};

/** Used when the home has no type, or a type added after this map was written. */
const FALLBACK_SLUGS: IllustrationSlug[] = [
    'lake-house',
    'country-farmhouse',
    'desert-bungalow',
    'coastal-cottage',
    'suburban-family-home',
];

export function illustrationSlugFor(
    home: Pick<HomeResponse, 'id' | 'home_type'>,
): IllustrationSlug {
    const code = home.home_type?.code;

    return illustrationSlugForSeed(
        home.id,
        code ? SLUG_BY_HOME_TYPE[code] : undefined,
        FALLBACK_SLUGS,
    );
}

/**
 * The image to show for a home, or undefined when there is nothing to show.
 * Callers must handle undefined — see the module note on assets.
 */
export function homeIllustration(
    home: Pick<HomeResponse, 'id' | 'home_type' | 'cover_photo_url'>,
): string | undefined {
    if (home.cover_photo_url) return home.cover_photo_url;

    return illustrationUrl(illustrationSlugFor(home));
}
